import { useForm } from "react-hook-form";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "~/common/mui";

interface QuoteFormData {
  recipientNote: string;
}

interface RequestConfigurationQuoteDialogProps {
  open: boolean;
  title: string;
  body: string;
  recipientLabel: string;
  recipient: string;
  noteLabel: string;
  info: string;
  mandatoryText: string;
  cancelLabel: string;
  submitLabel: string;
  submitError: string;
  onClose: () => void;
  onSubmit: (recipientNote: string) => Promise<void>;
}

export const RequestConfigurationQuoteDialog = ({
  open,
  title,
  body,
  recipientLabel,
  recipient,
  noteLabel,
  info,
  mandatoryText,
  cancelLabel,
  submitLabel,
  submitError,
  onClose,
  onSubmit,
}: RequestConfigurationQuoteDialogProps) => {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuoteFormData>({ defaultValues: { recipientNote: "" } });

  const submit = async ({ recipientNote }: QuoteFormData) => {
    try {
      await onSubmit(recipientNote);
      reset();
      onClose();
    } catch (error) {
      console.error(error);
      setError("root", { message: submitError });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="desktop-s"
      fullWidth
      slotProps={{ paper: { component: "form", onSubmit: handleSubmit(submit) } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Typography>{body}</Typography>
          <Typography variant="bodySmall" color="text.secondary">
            {recipientLabel}
          </Typography>
          <Typography>{recipient}</Typography>
          <TextField {...register("recipientNote")} multiline minRows={5} fullWidth label={noteLabel} />
          <Alert severity="info">{info}</Alert>
          {errors.root && <Alert severity="error">{errors.root.message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Typography variant="bodySmall">{mandatoryText}</Typography>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose} disabled={isSubmitting}>
            {cancelLabel}
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {submitLabel}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
